import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  computed,
  signal,
  inject,
} from "@angular/core";
import {
  ActivatedRoute,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
  RouterOutlet,
} from "@angular/router";
import { Observable, Subject, filter, map, startWith, takeUntil } from "rxjs";
import { monitoredDeprecatedUrls } from "src/app/app.routing";
import { ErrorsService } from "src/app/services/errors.service";
import { StorebrandOnlyRouterWatcherService } from "src/app/services/storebrand-only-router-watcher.service";
import { RedirectWarning } from "src/app/utils/errors";
import { Monitoring } from "src/app/utils/monitoring";
import { memoizeObject$ } from "src/app/utils/rxjs/select";
import { CookieService, Cookies } from "./services/cookie.service";
import { EngagementsService } from "./services/engagements.service";
import { FeatureToggleService } from "./services/feature-toggle.service";
import { MigrateOtherPensionToSinglePeriodService } from "./services/migrate-other-pension-to-single-period.service";
import { MigrateSimulationParametersService } from "./services/migrate-simulation-parameters.service";
import { PublicPensionConsentsPrognosisTriggerService } from "./services/prognoses-services/public-pension-consents-prognosis-trigger.service";
import {
  DisplayMode,
  GlobalQueryParam,
  getIsNavigationEnd,
  getIsNavigationEndOrCancelOrError,
} from "./utils/router.utils";
import { isNotStageP } from "./utils/storebrand-staging";
import { DatadogService } from "./services/datadog.service";
import { RefreshModalHostComponent } from "src/app/modules/shared/components/refresh-modal/refresh-modal.component";
import { AsyncPipe } from "@angular/common";
import { INCOME_STRATEGIES, IncomeService } from "src/app/services/income/income.service";
import { IncomePublicPension } from "src/app/services/income/strategies/income-public-pension";
import { IncomeEmployer } from "src/app/services/income/strategies/income-employer";
import { IncomeUser } from "src/app/services/income/strategies/income-user";
import { LoadingIndicatorDotsModule } from "@storeblocks/loading-indicator-dots-ng";
import { ErrorsComponent } from "src/app/components/errors/errors.component";
import { RoleErrorsComponent } from "src/app/components/role-errors/role-errors.component";
import { DevComponent } from "src/app/components/dev/dev.component";
import { MenuComponent } from "src/app/components/menu/menu.component";
import { BreadcrumbsComponent } from "src/app/components/breadcrumbs/breadcrumbs.component";
import { DataTrackingService } from "./services/data-tracking.service";
import { HotjarService } from "./services/hotjar.service";
import { UtmService } from "src/app/services/utm.service";
import { AllPagesAlertComponent } from "src/app/components/all-pages-alert/all-pages-alert.component";
import { SurfaceModule } from "@storeblocks/surface-ng";
import { FeatureBranchBannerComponent } from "src/app/modules/shared/components/feature-branch-banner/feature-branch-banner/feature-branch-banner.component";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  imports: [
    FeatureBranchBannerComponent,
    RouterOutlet,
    MenuComponent,
    ErrorsComponent,
    RefreshModalHostComponent,
    AsyncPipe,
    LoadingIndicatorDotsModule,
    ErrorsComponent,
    RoleErrorsComponent,
    DevComponent,
    BreadcrumbsComponent,
    AllPagesAlertComponent,
    SurfaceModule,
  ],
  providers: [
    CookieService,
    StorebrandOnlyRouterWatcherService,
    EngagementsService,
    MigrateOtherPensionToSinglePeriodService,
    MigrateSimulationParametersService,
    PublicPensionConsentsPrognosisTriggerService,
    DatadogService,
    IncomeService,
    IncomePublicPension,
    IncomeEmployer,
    IncomeUser,
    {
      provide: INCOME_STRATEGIES,
      useFactory: (
        publicPension: IncomePublicPension,
        employer: IncomeEmployer,
        user: IncomeUser,
      ): (IncomePublicPension | IncomeEmployer | IncomeUser)[] => {
        return [publicPension, employer, user];
      },
      deps: [IncomePublicPension, IncomeEmployer, IncomeUser],
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly cookieService = inject(CookieService);
  private readonly featureToggleService = inject(FeatureToggleService);
  private readonly migrateOtherPensionToSinglePeriodService = inject(MigrateOtherPensionToSinglePeriodService);
  private readonly migrateSimulationParametersService = inject(MigrateSimulationParametersService);
  private readonly storebrandOnlyRouterWatcherService = inject(StorebrandOnlyRouterWatcherService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly engagementsService = inject(EngagementsService);
  private readonly publicPensionConsentsPrognosisTriggerService = inject(PublicPensionConsentsPrognosisTriggerService);
  private readonly datadogService = inject(DatadogService);
  private readonly dataTrackingService = inject(DataTrackingService);
  private readonly hotjarService = inject(HotjarService);
  private readonly utmService = inject(UtmService);

  // 1. Private instance fields (DI and other private fields)
  private readonly destroy$ = new Subject<void>();
  private readonly currentUrl = signal<string>("");

  // 2. Public instance fields
  readonly errorsService = inject(ErrorsService);
  // Used from the template to force a re-check when router-outlet activates.
  readonly cdr = inject(ChangeDetectorRef);
  public readonly showBreadcrumbs$: Observable<boolean>;
  public readonly showDevPanel$: Observable<boolean>;
  public readonly navigationEnd$: Observable<NavigationEnd | NavigationCancel | NavigationError>;
  public readonly hasCriticalErrors$: Observable<boolean>;
  public readonly hasRoleErrors$: Observable<boolean>;
  public readonly isDashboardPage: () => boolean;

  constructor() {
    this.showDevPanel$ = this.cookieService
      .watch$(Cookies.ActivateDevPanel)
      .pipe(map((val) => val === "true" || isNotStageP()));

    this.hasCriticalErrors$ = this.errorsService.hasCriticalErrors$.pipe(startWith(false));
    this.hasRoleErrors$ = this.errorsService.hasRoleErrors$.pipe(startWith(false));

    this.navigationEnd$ = memoizeObject$(this.router.events.pipe(filter(getIsNavigationEndOrCancelOrError)));

    this.showBreadcrumbs$ = this.getShowBreadcrumbs(this.navigationEnd$);

    this.isDashboardPage = computed(() => {
      const url = this.currentUrl();
      // Dashboard is on root path, so it should be exactly "/" or empty after root
      return url === "/" || url === "" || url.split("?")[0] === "/";
    });

    this.datadogService.init();
  }

  public ngOnInit(): void {
    this.warnAboutDeprecatedUrl(this.navigationEnd$);

    // Track current URL for dashboard detection
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => (event as NavigationEnd).url),
        takeUntil(this.destroy$),
      )
      .subscribe((url) => {
        this.currentUrl.set(url);
      });

    // Set initial URL
    this.currentUrl.set(this.router.url);

    this.featureToggleService.init(this.destroy$).subscribe();
    this.migrateOtherPensionToSinglePeriodService.init();
    this.migrateSimulationParametersService.init();
    this.storebrandOnlyRouterWatcherService.init();
    this.engagementsService.beginPersistingPrognosisParametersByEngagement().subscribe();
    this.publicPensionConsentsPrognosisTriggerService.evictPublicPensionPrognosisOnNewConsents().subscribe();
    this.publicPensionConsentsPrognosisTriggerService.evictAndFetchParametersOnOfaConsent().subscribe();
    this.dataTrackingService.sendDataTracking().subscribe();
    this.hotjarService.init();
    this.utmService.getUTMFieldsAndSaveToLocalStorage();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Monitor certain redirects so we're able to catch
   * and fix old/deprecated links that may be removed in the future
   */
  private warnAboutDeprecatedUrl(routerEvent$: Observable<Event>): void {
    routerEvent$
      .pipe(
        filter(getIsNavigationEnd),
        filter(({ url }) => monitoredDeprecatedUrls.some((route) => url.includes(route))),
      )
      .subscribe((event) => {
        Monitoring.warn(
          new RedirectWarning(`[AppComponent::warnAboutDeprecatedUrl] ${event.url} => ${event.urlAfterRedirects}`),
        );
      });
  }

  private getShowBreadcrumbs(event$: Observable<Event>): Observable<boolean> {
    return event$.pipe(
      map(() => this.activatedRoute.snapshot.queryParamMap.get(GlobalQueryParam.DisplayMode)),
      map((displayMode) => displayMode !== DisplayMode.App),
    );
  }
}
